import { useTranslation } from 'react-i18next';
import quickTools from '@/components/quicktools/tools.json';
import { useQuickToolPrices } from '@/components/quicktools/QuickToolCards';
import { money, type QuickToolKey } from '@/lib/quicktools-api';
import { STUDIOS, studioById, type StudioId } from '@/lib/studios';

// EVERY TOOL, BY WHAT IT IS FOR (owner 2026-10-16: the pay-per-file tools are
// not "stuffed in a hidden folder that says quick tools"; every tool goes in
// the general category it belongs to, promoted and visible next to the
// others). One list for the desktop left menu, the phone menu and Create
// sheet, the search menu and the studio pages' "more tools".

export type CatalogCategory = 'music' | 'creators' | 'business' | 'family' | 'editor';

export interface CatalogTool {
  /** A studio id, or `qt-<key>` for a pay-per-file tool. */
  id: string;
  studio?: StudioId;
  quick?: QuickToolKey;
  path: string;
  poster: string;
  /** The output moving, where the tool has a clip. */
  clip?: string;
  /** Several outputs, all shown at once. */
  gallery?: { src: string; poster: string }[];
  name: string;
  /** One line: what you get. */
  line: string;
  /** What it costs: "$99", "25 cr", "$3", "Free". */
  price: string;
  /** Short price for the narrow left menu. */
  shortPrice: string;
  free: boolean;
}

const LAYOUT: { key: CatalogCategory; items: (StudioId | QuickToolKey)[] }[] = [
  { key: 'music', items: ['music', 'canvas', 'hooks'] },
  { key: 'creators', items: ['story', 'faceless', 'captions'] },
  { key: 'business', items: ['ads'] },
  { key: 'family', items: ['storybook', 'vows', 'tapes'] },
  { key: 'editor', items: ['editor'] },
];

const isStudio = (id: string): id is StudioId => STUDIOS.some((s) => s.id === id);

export function useToolCatalog(): { key: CatalogCategory; tools: CatalogTool[] }[] {
  const { t, i18n } = useTranslation();
  const prices = useQuickToolPrices();
  return LAYOUT.map(({ key, items }) => ({
    key,
    tools: items.map((id): CatalogTool => {
      if (isStudio(id)) {
        const s = studioById(id);
        return {
          id, studio: id, path: s.path(i18n.language), poster: s.poster, clip: s.clip,
          name: t(`studios.${s.key}.name`), line: t(`studios.${s.key}.door`),
          price: t(`studios.${s.key}.entry`), shortPrice: t(`studios.${s.key}.railEntry`), free: s.free,
        };
      }
      const q = quickTools.tools[id as QuickToolKey];
      const cost = money(prices[id as QuickToolKey]);
      return {
        id: `qt-${id}`, quick: id as QuickToolKey, path: q.path, poster: q.sample.poster,
        clip: q.sample.type === 'video' ? q.sample.src : undefined,
        gallery: 'gallery' in q ? (q as { gallery?: { src: string; poster: string }[] }).gallery : undefined,
        name: t(`qt.tools.${id}.name`), line: t(`qt.tools.${id}.blurb`),
        price: `${cost} ${t('qt.perFile')}`, shortPrice: cost, free: false,
      };
    }),
  }));
}
